import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ArrowLeft } from "lucide-react";
import { PageHero } from "./Universita";
import { BLOG_POSTS, findPost } from "../data/blog";

export function Blog() {
  return (
    <div data-testid="page-blog">
      <PageHero
        eyebrow="Risorse & Guide"
        title="Il blog del Polo ECP Profili."
        sub="Guide, consigli e approfondimenti su laurea online, certificazioni e orientamento universitario."
      />

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="blog-grid">
            {BLOG_POSTS.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group lift-card rounded-3xl overflow-hidden bg-brand-surface border border-brand-line" data-testid={`blog-card-${p.slug}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white text-brand-navy text-[10px] font-bold tracking-wider uppercase">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex gap-4 text-xs text-brand-muted mb-3">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readTime}</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-xl text-brand-navy leading-tight group-hover:text-brand-emerald transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-brand-muted mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <div className="mt-5 text-sm font-bold text-brand-emerald inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Leggi articolo <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  if (!post) {
    return (
      <div className="pt-32 pb-20 text-center" data-testid="blog-not-found">
        <h2 className="font-heading font-extrabold text-3xl text-brand-navy">Articolo non trovato</h2>
        <Link to="/blog" className="btn-secondary mt-6 inline-flex">Torna al blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-post">
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 bg-brand-navy text-white overflow-hidden">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold mb-6" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Torna al blog
          </Link>
          <span className="inline-block px-2.5 py-1 rounded-full bg-brand-emerald text-white text-[11px] font-bold tracking-wider uppercase">
            {post.category}
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight">{post.title}</h1>
          <div className="mt-6 flex gap-5 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime} di lettura</span>
          </div>
        </div>
      </section>

      <article className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 prose-content">
          <p className="text-lg text-brand-ink font-semibold leading-relaxed mb-6">{post.excerpt}</p>
          {post.content.map((para, i) => (
            <p
              key={i}
              className="text-brand-ink text-[17px] leading-[1.85] mb-5"
              dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="text-brand-navy">$1</strong>') }}
            />
          ))}

          <div className="mt-12 rounded-3xl bg-brand-surface border border-brand-line p-8 text-center">
            <h3 className="font-heading font-extrabold text-2xl text-brand-navy">Vuoi saperne di più?</h3>
            <p className="text-brand-muted mt-2">Parla con un tutor del Polo ECP Profili.</p>
            <Link to="/contatti" className="btn-primary mt-5 inline-flex" data-testid="blogpost-cta">
              Richiedi Informazioni <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
